import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { baseQueryWithClient } from "@/redux/baseQuery";
import { ClientProfile, UpdateClientProfileParams } from "./types";

export const clientProfileApi = createApi({
  reducerPath: "clientProfileApi",
  baseQuery: baseQueryWithClient(),
  tagTypes: ["ClientProfile"],
  endpoints: (builder) => ({
    getClientProfile: builder.query<ClientProfile, { accountId?: string } | void>({
      query: (arg) => ({ url: "/profile", accountId: arg?.accountId }),
      providesTags: ["ClientProfile"],
    }),
    updateClientProfile: builder.mutation<ClientProfile, UpdateClientProfileParams>({
      // accountId targets the managed client via baseQueryWithClient; kept out of the body.
      query: ({ accountId, ...body }) => ({
        url: "/profile",
        method: "PATCH",
        body,
        accountId,
      }),
      // Optimistically reflect notification-toggle changes so the Toggle flips on click
      // instead of waiting for the PATCH + refetch round-trip. Reverts if the request fails.
      // Scoped to `notifications` so the form-driven callers (company/preferences/emails)
      // keep their existing save-then-refetch behaviour.
      async onQueryStarted(patch, { dispatch, queryFulfilled }) {
        if (!patch.notifications) return;
        // Target the same cache entry the screen reads: self-service uses the `undefined` arg, while
        // the operator-managed view (ClientScopeProvider) queries with `{ accountId }`. Without this
        // the optimistic flip would silently miss the managed cache and the toggle would lag.
        const queryArg = patch.accountId ? { accountId: patch.accountId } : undefined;
        const patchResult = dispatch(
          clientProfileApi.util.updateQueryData("getClientProfile", queryArg, (draft) => {
            if (draft.notifications) Object.assign(draft.notifications, patch.notifications);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: ["ClientProfile"],
    }),
  }),
});

export const { useGetClientProfileQuery, useUpdateClientProfileMutation } = clientProfileApi;
